/**
 * Admin API client — thin call into the shared factory.
 * Behaviour (timezone header, FormData passthrough, 429 retry-after,
 * refresh-token rotation, error toasts) is owned by `@shared/http`.
 */
import { createApiClient } from "@shared/http";
import { toast } from "sonner";
import {
	endNonAdminSession,
	isNonAdminSessionError,
} from "./non-admin-session";
import { adminTokenManager } from "./token-manager";

declare module "axios" {
	interface AxiosRequestConfig {
		/**
		 * The caller shows this request's failure itself, inline, so the
		 * error toast is skipped for it.
		 */
		inlineErrors?: boolean;
	}
}

function resolveApiUrl(): string {
	const configuredUrl = import.meta.env.VITE_API_URL?.trim();
	if (configuredUrl) return configuredUrl.replace(/\/$/, "");

	if (typeof window !== "undefined" && window.location.protocol === "file:") {
		return "http://localhost:3000";
	}

	return "";
}

export const apiClient = createApiClient({
	baseUrl: resolveApiUrl(),
	tokenManager: adminTokenManager,
	// The console's own refresh route. Under /api/v1/admin the backend reads
	// and rotates only the admin session cookie, so a refresh here never
	// touches a Finishd app session in the same browser (#457).
	refreshEndpoint: "/api/v1/admin/auth/refresh-token",
	onUnauthorized: () => {
		// Hard redirect to admin login. We don't try to be clever about route
		// state; the operator re-enters their phone and re-authenticates.
		if (typeof window !== "undefined") {
			window.location.href = "/login";
		}
	},
	onError: (status, message, request) => {
		if (request?.inlineErrors) return;
		// No toast: the login page's notice explains it.
		if (isNonAdminSessionError(status, message)) {
			endNonAdminSession();
			return;
		}
		// One toast per distinct failure while it is on screen: the approvals
		// page runs four queue queries, each retried once, and one failure
		// would otherwise stack eight. A repeat updates the visible toast.
		toast.error(message, { id: `http-${status}-${message}` });
	},
	onRateLimited: (retryAfter) => {
		toast.error(
			`Too many requests. Please try again in ${retryAfter} seconds.`,
		);
	},
	onNetworkError: () => {
		toast.error("Network error. Please check your connection.");
	},
});
